import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { zip } from 'fflate';
import {
  projectEssentialApi,
  fetchPreviewBlob,
  type ProjectFileGroup,
  type ProjectFileItem,
} from '../services/api';
import FilePreviewDialog from './FilePreviewDialog';
import {
  logProjectResourceFilePreviewed,
  logProjectResourceFileDownloaded,
} from '../services/analytics';
import {
  FilePreviewThumbnail,
  buildProjectDownloadUrl,
  formatUploadedDate,
  getFileKind,
  isPdfFile,
  isPreviewableImageFile,
  isPreviewableFile,
  triggerProjectDownload,
  useTouchPreviewDevice,
  formatLabelForWrap,
} from './fileVisuals';

interface ProjectFilesPanelProps {
  projectId: string;
  /**
   * Optional pre-fetched files data. When provided, the panel skips its own
   * fetch and renders from these values — useful for hoisting the fetch into
   * the parent so a file count can be displayed in the surrounding tab label.
   */
  data?: { groups: ProjectFileGroup[]; total: number } | null;
  loading?: boolean;
  error?: string | null;
  /** When false, hides row/group checkboxes and the bulk download toolbar. */
  enableBulkSelection?: boolean;
}

// Map the short Category codes (varchar(10)) to friendly labels. Unknown codes
// fall back to the raw code with simple title-casing so new categories surface
// without a code change.
const CATEGORY_LABELS: Record<string, string> = {
  FACTSHEET: 'Factsheets',
  BROCHURE: 'Brochures',
  FLOORPLAN: 'Floor Plans',
  SITEPLAN: 'Site Plans',
  PRICELIST: 'Price Lists',
  EBOOK: 'eBooks',
  VIDEO: 'Videos',
  EDM: 'EDMs',
  // Huttons-internal short codes used by the portal's file-category
  // dropdown. We expand them here so the dialog reads naturally instead
  // of surfacing the raw acronyms.
  DPI: 'Developer Project Info',
  PF: 'Project Forms',
  SMC: 'Sales & Marketing Collaterals',
  STS: 'Saleskit / Training Slides',
  PU: 'Pricing Update',
  OTH: 'Others',
  OTHER: 'Others',
};

function prettyCategory(code: string): string {
  const upper = code.toUpperCase();
  if (CATEGORY_LABELS[upper]) return CATEGORY_LABELS[upper];
  return upper
    .toLowerCase()
    .split(/[_\s]+/)
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

function sanitizeZipPathPart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    || 'file';
}

/** Avoid collisions when two selected files share the same display name. */
function uniqueZipEntryName(desired: string, used: Set<string>): string {
  if (!used.has(desired)) {
    used.add(desired);
    return desired;
  }
  const slash = desired.lastIndexOf('/');
  const dir = slash >= 0 ? desired.slice(0, slash + 1) : '';
  const leaf = slash >= 0 ? desired.slice(slash + 1) : desired;
  const dot = leaf.lastIndexOf('.');
  const base = dot > 0 ? leaf.slice(0, dot) : leaf;
  const ext = dot > 0 ? leaf.slice(dot) : '';
  let i = 2;
  let next = `${dir}${base} (${i})${ext}`;
  while (used.has(next)) {
    i += 1;
    next = `${dir}${base} (${i})${ext}`;
  }
  used.add(next);
  return next;
}

function zipFilesAsync(files: Record<string, Uint8Array>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(files, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

async function blobFromPreviewUrl(targetUrl: string): Promise<Blob> {
  const { objectUrl } = await fetchPreviewBlob(targetUrl);
  try {
    const response = await fetch(objectUrl);
    if (!response.ok) throw new Error(`Blob read failed: ${response.status}`);
    return await response.blob();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function FileRow({
  file,
  projectId,
  checked,
  onToggle,
  showCheckbox = true,
}: {
  file: ProjectFileItem;
  projectId: string;
  checked: boolean;
  onToggle: () => void;
  showCheckbox?: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();

  // A row is downloadable as long as we have a filename — the actual
  // bytes are served by the Huttons portal endpoint, constructed from
  // `projectId` + `filename`. We no longer rely on `file.url` for
  // downloads, but still expose it via `previewUrl` for the inline PDF
  // thumbnail.
  const filename = file.filename || file.displayName;
  const disabled = !filename;
  const downloadUrl = filename ? buildProjectDownloadUrl(projectId, filename) : null;
  // Prefer the canonical download endpoint for thumbnails — same bytes
  // the Download button serves, and reliably proxied for inline preview.
  const previewUrl = downloadUrl ?? file.url;
  const uploadedAt = formatUploadedDate(file.updatedAt) || formatUploadedDate(file.createdAt);
  const { label: typeLabel } = getFileKind(filename);
  // Build the bottom-aligned meta line ("PDF · Uploaded May 2"). Mirrors
  // `DownloadFileRow` so project resources match the marketing surfaces.
  const metaPieces: string[] = [];
  if (typeLabel) metaPieces.push(typeLabel);
  if (uploadedAt) metaPieces.push(`Uploaded ${uploadedAt}`);
  const metaLine = metaPieces.join(' · ');
  const previewable = isPreviewableFile(filename);
  const isImage = isPreviewableImageFile(filename);
  const isPdf = isPdfFile(filename);
  const rowOpensPreview =
    previewable && (!touchPreview || isImage || isPdf);
  const showDownloadButton =
    !previewable || (touchPreview && !isImage && !isPdf);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleDownload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (filename) {
      logProjectResourceFileDownloaded(file.id);
      triggerProjectDownload(projectId, filename, file.displayName);
    }
  };

  const handleRowClick = () => {
    if (previewUrl && rowOpensPreview) {
      logProjectResourceFilePreviewed(file.id);
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <Box
        onClick={handleRowClick}
        role={previewUrl && rowOpensPreview ? 'button' : undefined}
        tabIndex={previewUrl && rowOpensPreview ? 0 : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: { xs: 1, sm: 1.5 },
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          border: '1px solid var(--color-border-strong)',
          bgcolor: 'var(--color-surface)',
          cursor: previewUrl && rowOpensPreview ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            borderColor: 'var(--color-accent)',
            boxShadow: '0 0 0 1px var(--color-accent)',
          },
        }}
      >
        {showCheckbox ? (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={checked}
              onChange={onToggle}
              disabled={disabled}
              size="small"
              sx={{
                color: 'var(--color-text-muted)',
                '&.Mui-checked': { color: 'var(--color-accent-strong)' },
                p: 0.5,
              }}
            />
          </Box>
        ) : null}
        <FilePreviewThumbnail
          filename={filename}
          url={previewUrl}
          size={isMobile ? 'small' : 'large'}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.25,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              overflowWrap: 'break-word',
              wordBreak: 'normal',
              whiteSpace: 'normal',
            }}
            title={file.displayName}
          >
            {formatLabelForWrap(file.displayName)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {isMobile ? (
            (typeLabel || uploadedAt) && (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                {typeLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      fontSize: '0.78rem',
                    }}
                  >
                    {typeLabel}
                  </Typography>
                )}
                {uploadedAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      lineHeight: 1.3,
                    }}
                  >
                    Uploaded {uploadedAt}
                  </Typography>
                )}
              </Stack>
            )
          ) : (
            metaLine && (
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--color-text-muted)',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  fontSize: { xs: '0.78rem', sm: '0.82rem' },
                  mt: 1,
                }}
              >
                {metaLine}
              </Typography>
            )
          )}
        </Box>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ flexShrink: 0, alignSelf: 'stretch', py: 0.5 }}
        >
          {showDownloadButton && (
            <Tooltip title="Download file" arrow placement="left">
              <span>
                <IconButton
                  onClick={handleDownload}
                  disabled={disabled}
                  aria-label="Download file"
                  size="small"
                  sx={{
                    color: 'var(--color-accent)',
                    p: 0.5,
                    '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                    '&.Mui-disabled': { color: 'var(--color-border-strong)' },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {previewUrl && rowOpensPreview && (
        <FilePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={file.displayName}
          url={previewUrl}
          filename={filename}
          onDownloadClick={() => logProjectResourceFileDownloaded(file.id)}
        />
      )}
    </>
  );
}

export default function ProjectFilesPanel({
  projectId,
  data,
  loading: loadingProp,
  error: errorProp,
  enableBulkSelection = true,
}: ProjectFilesPanelProps) {
  // If the parent provides data we use those values directly; otherwise the
  // panel falls back to fetching its own (legacy behaviour).
  const externallyManaged = data !== undefined || loadingProp !== undefined || errorProp !== undefined;
  const [internalGroups, setInternalGroups] = useState<ProjectFileGroup[]>([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    if (externallyManaged) return;
    let mounted = true;
    setInternalLoading(true);
    setInternalError(null);
    setSelected(new Set());
    projectEssentialApi
      .getProjectFiles(projectId)
      .then((res) => {
        if (mounted) setInternalGroups(res.groups);
      })
      .catch((err) => {
        if (mounted) setInternalError(err?.message || 'Failed to load project files');
      })
      .finally(() => {
        if (mounted) setInternalLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [projectId, externallyManaged]);

  // Reset selection when the underlying file list changes.
  useEffect(() => {
    if (externallyManaged) setSelected(new Set());
  }, [data, externallyManaged]);

  const groups = externallyManaged ? (data?.groups ?? []) : internalGroups;
  const loading = externallyManaged ? !!loadingProp : internalLoading;
  const error = externallyManaged ? errorProp ?? null : internalError;

  // Flatten all downloadable files for the master toolbar.
  const downloadable = useMemo(
    () => groups.flatMap((g) => g.items).filter((f) => !!f.url),
    [groups],
  );

  const selectedFiles = useMemo(
    () => downloadable.filter((f) => selected.has(f.id)),
    [downloadable, selected],
  );

  const allSelected =
    downloadable.length > 0 && selectedFiles.length === downloadable.length;
  const someSelected = selectedFiles.length > 0 && !allSelected;

  const toggleFile = (file: ProjectFileItem) => {
    if (!file.url) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === downloadable.length) return new Set();
      return new Set(downloadable.map((f) => f.id));
    });
  };

  const toggleGroup = (group: ProjectFileGroup) => {
    const groupDownloadable = group.items.filter((f) => !!f.url);
    const allInGroupSelected =
      groupDownloadable.length > 0 &&
      groupDownloadable.every((f) => selected.has(f.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allInGroupSelected) {
        groupDownloadable.forEach((f) => next.delete(f.id));
      } else {
        groupDownloadable.forEach((f) => next.add(f.id));
      }
      return next;
    });
  };

  const downloadSelected = async () => {
    if (selectedFiles.length === 0 || zipping) return;

    // Single file: keep a direct download (no zip overhead).
    if (selectedFiles.length === 1) {
      const f = selectedFiles[0];
      const filename = f.filename || f.displayName;
      if (!filename) return;
      logProjectResourceFileDownloaded(f.id);
      const url = buildProjectDownloadUrl(projectId, filename);
      try {
        const { objectUrl } = await fetchPreviewBlob(url);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = f.displayName || filename;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      } catch {
        triggerProjectDownload(projectId, filename, f.displayName);
      }
      return;
    }

    // Multiple files: fetch via proxy, pack into one zip, download once.
    // Avoids N separate downloads and the portal popup-blocker path.
    setZipping(true);
    try {
      const usedNames = new Set<string>();
      const zipEntries: Record<string, Uint8Array> = {};

      await Promise.all(
        selectedFiles.map(async (f) => {
          const filename = f.filename || f.displayName;
          if (!filename) return;
          logProjectResourceFileDownloaded(f.id);
          const url = buildProjectDownloadUrl(projectId, filename);
          try {
            const blob = await blobFromPreviewUrl(url);
            const bytes = new Uint8Array(await blob.arrayBuffer());
            const folder = sanitizeZipPathPart(prettyCategory(f.category || 'Others'));
            const leaf = sanitizeZipPathPart(f.displayName || filename);
            const entry = uniqueZipEntryName(`${folder}/${leaf}`, usedNames);
            zipEntries[entry] = bytes;
          } catch {
            // Skip failed files rather than aborting the whole zip.
          }
        }),
      );

      const entryKeys = Object.keys(zipEntries);
      if (entryKeys.length === 0) return;

      const zipped = await zipFilesAsync(zipEntries);
      const objectUrl = URL.createObjectURL(
        new Blob([zipped], { type: 'application/zip' }),
      );
      try {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `${sanitizeZipPathPart(projectId)}-files.zip`;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      }
    } finally {
      setZipping(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 4, sm: 6 }, textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'var(--color-accent-soft)',
            color: 'var(--color-accent-strong)',
          }}
        >
          <FolderOpenOutlinedIcon />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
          Files
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}>
          There are no related files for this project.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {enableBulkSelection ? (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            mb: 1,
            bgcolor: 'var(--color-surface-elevated-strong)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleAll}
            disabled={downloadable.length === 0}
            size="small"
            sx={{
              color: 'var(--color-text-muted)',
              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'var(--color-accent-strong)' },
            }}
          />
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', flex: 1 }}>
            {selectedFiles.length > 0
              ? `${selectedFiles.length} selected`
              : `Select all • ${downloadable.length} file${downloadable.length === 1 ? '' : 's'}`}
          </Typography>
          <Button
            onClick={downloadSelected}
            variant="contained"
            size="small"
            startIcon={
              zipping ? (
                <CircularProgress size={16} thickness={5} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={selectedFiles.length === 0 || zipping}
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': { bgcolor: 'var(--color-accent-strong)' },
              '&.Mui-disabled': {
                bgcolor: 'var(--color-surface-elevated)',
                color: 'var(--color-text-muted)',
              },
            }}
          >
            {zipping
              ? 'Preparing zip…'
              : selectedFiles.length > 1
                ? `Download zip (${selectedFiles.length})`
                : 'Download'}
          </Button>
        </Box>
      ) : null}

      {/* Category groups */}
      <Stack spacing={2}>
        {groups.map((group) => {
          const groupDownloadable = group.items.filter((f) => !!f.url);
          const allInGroupSelected =
            groupDownloadable.length > 0 &&
            groupDownloadable.every((f) => selected.has(f.id));
          const someInGroupSelected =
            !allInGroupSelected && groupDownloadable.some((f) => selected.has(f.id));

          return (
            <Accordion
              key={group.category}
              defaultExpanded
              disableGutters
              elevation={0}
              square
              sx={{
                border: '1px solid var(--color-border)',
                borderRadius: '8px !important',
                overflow: 'hidden',
                bgcolor: 'var(--color-surface)',
                color: 'var(--color-text)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-muted)' }} />}
                sx={{
                  bgcolor: 'var(--color-surface-elevated)',
                  minHeight: 48,
                  px: 1.5,
                  py: 0.5,
                  '&.Mui-expanded': {
                    borderBottom: '1px solid var(--color-border)',
                  },
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    m: '0 !important',
                  },
                }}
              >
                {enableBulkSelection ? (
                  <Checkbox
                    checked={allInGroupSelected}
                    indeterminate={someInGroupSelected}
                    disabled={groupDownloadable.length === 0}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleGroup(group);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    size="small"
                    sx={{
                      color: 'var(--color-text-muted)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'var(--color-accent-strong)',
                      },
                      p: 0.5,
                    }}
                  />
                ) : null}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'var(--color-text-strong)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontSize: '0.78rem',
                    flex: 1,
                  }}
                >
                  {prettyCategory(group.category)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mr: 1 }}>
                  {group.items.length} file{group.items.length === 1 ? '' : 's'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 1.5,
                  bgcolor: 'var(--color-surface)',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: { xs: 1, sm: 1.25 },
                  }}
                >
                  {group.items.map((file) => (
                    <FileRow
                      key={file.id}
                      file={file}
                      projectId={projectId}
                      checked={selected.has(file.id)}
                      onToggle={() => toggleFile(file)}
                      showCheckbox={enableBulkSelection}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}
